import { SELECTED_SCRIPT_KEY, TOKEN_KEY } from '@/config/constants'

const CHANGE_KEY = 'yuyuan-workbench-script-change'
const CHANGE_EVENT = 'yuyuan-workbench-script-change'
const queryPages = new Set(['CanvasList', 'SmartCanvas', 'MusicGenerate', 'SubtitleRemoval', 'VoiceService', 'VolcAsset', 'ScriptAssetGenerate'])

export function routeScriptId(route) {
  const value = route.params?.scriptId ?? route.query?.scriptId
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

export function scriptSelectionDestination(route, scriptId) {
  const id = String(scriptId || '')
  if (!id || routeScriptId(route) === id) return null
  if (['VideoStudio', 'VideoDetail'].includes(route.name)) return { name: 'VideoStudio', params: { scriptId: id } }
  if (['ImageGenerate', 'ImageGenerateStudio'].includes(route.name)) return { name: 'ImageGenerateStudio', params: { scriptId: id } }
  if (!queryPages.has(route.name)) return null
  // A canvas document belongs to its original project. Never relabel it.
  if (route.name === 'SmartCanvas') return { name: 'CanvasList', query: { scriptId: id } }
  return { path: route.path, query: { ...route.query, scriptId: id }, hash: route.hash }
}

function readChange(browser) {
  try { return JSON.parse(browser.localStorage.getItem(CHANGE_KEY) || 'null') } catch (_) { return null }
}

export function selectWorkspaceScript(script, browser = window) {
  if (script?.id == null) return
  const change = { script, sessionToken: browser.localStorage.getItem(TOKEN_KEY), nonce: `${Date.now()}-${Math.random()}` }
  try {
    browser.localStorage.setItem(SELECTED_SCRIPT_KEY, JSON.stringify(script))
    browser.localStorage.setItem(CHANGE_KEY, JSON.stringify(change))
  } catch (_) { /* Same-tab selection still works when storage is unavailable. */ }
  browser.dispatchEvent(new browser.CustomEvent(CHANGE_EVENT, { detail: change }))
}

export function subscribeWorkspaceScript(callback, browser = window) {
  const sessionToken = browser.localStorage.getItem(TOKEN_KEY)
  const receive = change => {
    if (!sessionToken || sessionToken !== browser.localStorage.getItem(TOKEN_KEY) || change?.sessionToken !== sessionToken || change?.script?.id == null) return
    callback(change.script)
  }
  const storage = event => {
    if (event.key === CHANGE_KEY && event.storageArea === browser.localStorage) receive(readChange(browser))
  }
  const local = event => receive(event.detail)
  const resume = () => receive(readChange(browser))
  browser.addEventListener('storage', storage)
  browser.addEventListener(CHANGE_EVENT, local)
  browser.addEventListener('focus', resume)
  browser.addEventListener('pageshow', resume)
  return () => {
    browser.removeEventListener('storage', storage)
    browser.removeEventListener(CHANGE_EVENT, local)
    browser.removeEventListener('focus', resume)
    browser.removeEventListener('pageshow', resume)
  }
}

export function startWorkspaceScriptSync(router, browser = window) {
  let applying = false
  let pendingScript = null
  let disposed = false
  const sessionToken = browser.localStorage.getItem(TOKEN_KEY)
  const apply = async script => {
    pendingScript = script
    if (applying) return
    applying = true
    try {
      while (pendingScript && !disposed && sessionToken === browser.localStorage.getItem(TOKEN_KEY)) {
        const next = pendingScript
        pendingScript = null
        const destination = scriptSelectionDestination(router.currentRoute.value, next.id)
        if (!destination) continue
        try { await router.replace(destination) } catch (error) { console.error('同步剧本失败:', error) }
      }
    } finally { applying = false }
  }
  const stopSelection = subscribeWorkspaceScript(apply, browser)
  // A tab may have been suspended or still loading when the change was sent.
  router.isReady().then(() => {
    const change = readChange(browser)
    if (!disposed && sessionToken && sessionToken === browser.localStorage.getItem(TOKEN_KEY) && change?.sessionToken === sessionToken && change?.script?.id != null) apply(change.script)
  })
  const stopRoute = router.afterEach((to, from, failure) => {
    const id = routeScriptId(to)
    if (disposed || !sessionToken || sessionToken !== browser.localStorage.getItem(TOKEN_KEY) || failure || applying || !from.matched?.length || !id || id === routeScriptId(from)) return
    if (!['VideoStudio', 'ImageGenerateStudio', ...queryPages].includes(to.name)) return
    const latest = readChange(browser)
    if (latest?.sessionToken === browser.localStorage.getItem(TOKEN_KEY) && String(latest?.script?.id) === id) return
    selectWorkspaceScript({ id }, browser)
  })
  return () => { disposed = true; stopSelection(); stopRoute() }
}
